<template>
  <div ref="mapContainer" class="map-container">
    <slot></slot>
  </div>
</template>

<script>
import * as maplibregl from "maplibre-gl";
import { layers, namedFlavor } from "@protomaps/basemaps";
import "maplibre-gl/dist/maplibre-gl.css";

export default {
  name: "Map",
  provide() {
    return {
      // Provide a function to access the map and the readiness promise
      mapReady: () => this.mapReadyPromise,
    };
  },
  inject: ["tilesUrl", "cdnUrl", "mapLanguage"],
  props: {
    zoom: {
      type: Number,
      default: 4,
    },
    mapStyle: {
      type: String,
      default: "light",
    },
  },
  emits: ["boundsChanged", "mapMoved"],
  data() {
    return {
      map: null,
      mapReadyPromise: null,
      resolveMapReady: null,
      isDestroyed: false,
    };
  },
  created() {
    // Initialize the promise early
    this.mapReadyPromise = new Promise((resolve) => {
      this.resolveMapReady = resolve;
    });
  },
  mounted() {
    this.initMapLibreMap();
  },
  destroyed() {
    this.destroyMap();
  },
  unmounted() {
    this.destroyMap();
  },
  methods: {
    initMapLibreMap() {
      this.map = new maplibregl.Map({
        container: this.$refs.mapContainer,
        attributionControl: false,
        zoom: this.zoom,
        minZoom: 3,
        maxZoom: 13,
        style: {
          version: 8,
          glyphs: `${this.cdnUrl}/basemaps-assets/fonts/{fontstack}/{range}.pbf`,
          sprite: `${this.cdnUrl}/basemaps-assets/sprites/v4/${this.mapStyle}`,
          sources: {
            protomaps: {
              type: "vector",
              url: this.tilesUrl,
            },
          },
          layers: layers("protomaps", namedFlavor(this.mapStyle), {
            lang: this.mapLanguage,
          }),
        },
      });

      // Add zoom and rotation controls
      this.map.addControl(
        new maplibregl.NavigationControl({
          showZoom: true,
          showCompass: false,
        }),
      );

      // Add geolocate control
      this.map.addControl(
        new maplibregl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: false,
        }),
        "top-left",
      );

      this.map.on("load", () => {
        this.resolveMapReady(this.map);

        // Do an initial refine to correctly update the overall job count & filter options
        this.emitBoundsChanged();
      });

      this.map.on("moveend", this.emitBoundsChanged);

      this.map.on("dragend", () => {
        this.$emit("mapMoved");
      });
      this.map.on("zoomend", () => {
        this.$emit("mapMoved");
      });
    },
    changeMapStyle() {
      // Only needed in the RH to dynamically updater the map style in the preview
      if (this.map) {
        const styleConfig = {
          version: 8,
          glyphs: `${this.cdnUrl}/basemaps-assets/fonts/{fontstack}/{range}.pbf`,
          sprite: `${this.cdnUrl}/basemaps-assets/sprites/v4/${this.mapStyle}`,
          sources: {
            protomaps: {
              type: "vector",
              url: this.tilesUrl,
            },
          },
          layers: layers("protomaps", namedFlavor(this.mapStyle), {
            lang: this.mapLanguage,
          }),
        };

        this.map.setStyle(styleConfig);
      }
    },
    destroyMap() {
      if (!this.isDestroyed && this.map) {
        this.map.remove();
        this.isDestroyed = true;
        this.map = null;
      }
    },
    emitBoundsChanged() {
      const mapBounds = this.map.getBounds();

      const changedBounds = {
        northEast: {
          lat: mapBounds.getNorthEast().lat,
          lng: mapBounds.getNorthEast().lng,
        },
        southWest: {
          lat: mapBounds.getSouthWest().lat,
          lng: mapBounds.getSouthWest().lng,
        },
      };

      this.$emit("boundsChanged", changedBounds);
    },
    centerMap(lngLat, zoom) {
      if (this.map) {
        this.map.flyTo({
          center: lngLat,
          zoom: zoom ?? this.map.getZoom(), // default to current zoom
          duration: 500,
        });
      }
    },
  },
};
</script>
